import { Image as ImageIcon, Loader2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useUploadCostumeImageMutation } from '~entities/recital-manager/api/upload-costume-image';
import { type Performance } from '~entities/recital-manager/model/recital';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';

interface UploadCostumeImageProps {
  actId: string;
  uploadCostumeDialogOpen: boolean;
  setUploadCostumeDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedPerformance: Performance | null;
  setSelectedPerformance: React.Dispatch<
    React.SetStateAction<Performance | null>
  >;
}

export const UploadCostumeImage = ({
  actId,
  uploadCostumeDialogOpen,
  setUploadCostumeDialogOpen,
  selectedPerformance,
  setSelectedPerformance,
}: UploadCostumeImageProps) => {
  const [costumeImageFile, setCostumeImageFile] = useState<File | null>(null);

  const {
    mutateAsync: uploadCostumeImage,
    isPending: isUploadingCostumeImage,
  } = useUploadCostumeImageMutation();

  const handleUploadCostumeImage = async () => {
    if (!costumeImageFile || !selectedPerformance) return;
    try {
      await uploadCostumeImage({
        actId,
        performanceId: selectedPerformance._id,
        file: costumeImageFile,
      });
      toast.success('Costume image uploaded successfully!');
      setUploadCostumeDialogOpen(false);
      setSelectedPerformance(null);
      setCostumeImageFile(null);
    } catch (error) {
      console.error('Error uploading costume image:', error);
    }
  };

  return (
    <Dialog
      open={uploadCostumeDialogOpen}
      onOpenChange={setUploadCostumeDialogOpen}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Costume Image</DialogTitle>
          <DialogDescription>
            Upload costume image for &quot;{selectedPerformance?.name}&quot;
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          <div className='space-y-2'>
            <label className='text-sm font-medium'>Costume Image</label>
            <div className='flex items-center gap-2'>
              <Input
                type='file'
                accept='image/jpeg,image/png,image/webp'
                onChange={(e) => {
                  setCostumeImageFile(e.target.files?.[0] ?? null);
                }}
                className='cursor-pointer'
              />
            </div>
            {costumeImageFile && (
              <div className='flex items-center gap-2 rounded border p-2 text-sm'>
                <ImageIcon className='size-4' />
                <span className='flex-1'>{costumeImageFile.name}</span>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => {
                    setCostumeImageFile(null);
                  }}
                >
                  <X className='size-4' />
                </Button>
              </div>
            )}
            <p className='text-xs text-muted-foreground'>
              Accepted formats: JPEG, PNG, WebP (Max 5MB)
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => {
              setUploadCostumeDialogOpen(false);
              setCostumeImageFile(null);
              setSelectedPerformance(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadCostumeImage}
            disabled={!costumeImageFile || isUploadingCostumeImage}
          >
            {isUploadingCostumeImage && (
              <Loader2 className='size-4 animate-spin' />
            )}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
